/**
 * Decision 0144 (experiment 11, 0136 §4): a lead's brief as a live surface on Home. The lead writes the sections and
 * their refs; this page resolves every ref now (one lookup per brief: `GET …/refs`), so entries show live state:
 *
 * - **Your asks**: the real cards in their shape (question, recommendation, answer buttons), decided right here;
 *   answered ones stay, struck through, with who answered.
 * - **Waiting on you**: ranked by what each holds up (core `askRank` / `itemRank`); what blocks nothing folds into one
 *   line.
 * - **Didn't work**: each with what is needed from whom.
 * - **What happened**: the conclusion first, folding out to its evidence.
 * - **Next**, and free text.
 *
 * Each section ends in a reply line to the lead's chat with the section quoted (the composer opens with it, unsent).
 * A brief from before 0144 (markdown only) renders as before, with live chips. On a phone only "Your asks" is open.
 */
import { BRIEF_SECTION_TITLES, briefSectionRefs, refsIn } from "@minga/core"
import type { BriefSections, CardShape, ProposalDecision, RefView } from "@minga/protocol"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import {
  type ComponentType,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import { Markdown } from "@/components/markdown/lazy"
import { LinkProjectContext } from "@/components/markdown/link"
import type { MarkdownProps } from "@/components/markdown/markdown"
import { RefChip } from "@/components/markdown/ref-chip"
import { RefText } from "@/components/markdown/ref-text"
import {
  RefContext,
  RefDecideContext,
  RefLookupContext,
  type RefResolver,
  refTone,
} from "@/components/markdown/refs"
import { CardShapeView } from "@/components/refs/card-shape-view"
import { Glyph, MarkGlyph } from "@/components/refs/ref-icons"
import { ASK_MAX } from "@/lib/project-search"
import { decideCardHttp, refViewsQuery } from "@/lib/refs-api"
import { homeQuery } from "@/lib/settings-api"
import { useNarrow } from "@/lib/use-media-query"
import { cn } from "@/lib/utils"

type Views = ReadonlyMap<string, RefView>

/** The refs a brief names: its sections' refs, else the refs in its markdown. */
export const briefRefs = (body: string, sections: BriefSections | undefined): ReadonlyArray<string> =>
  sections ? briefSectionRefs(sections) : refsIn(body)

/**
 * "Waiting on you" ranked by what each holds up (the lead's order breaks ties); entries that hold nothing up are
 * listed apart, to fold into one line.
 */
export const rankWaitingViews = (
  refs: ReadonlyArray<string>,
  views: Views,
): { readonly top: ReadonlyArray<string>; readonly rest: ReadonlyArray<string> } => {
  const rows = refs.map((ref, i) => ({
    ref,
    i,
    rank: views.get(ref)?.rank ?? -1,
    holds: views.get(ref)?.holds ?? 0,
  }))
  rows.sort((a, b) => b.rank - a.rank || a.i - b.i)
  return {
    top: rows.filter((r) => r.holds > 0 || views.get(r.ref)?.card).map((r) => r.ref),
    rest: rows.filter((r) => !(r.holds > 0 || views.get(r.ref)?.card)).map((r) => r.ref),
  }
}

/** The chat link that opens the lead's composer with a section quoted (unsent): `?view=chat&ask=`. */
export const replyHref = (workspaceId: string, projectId: string, quote: string, text: string): string => {
  const body = `${quote
    .split("\n")
    .map((l) => `> ${l}`)
    .join("\n")}\n\n${text}`.slice(0, ASK_MAX)
  return `/w/${encodeURIComponent(workspaceId)}/p/${encodeURIComponent(projectId)}?view=chat&ask=${encodeURIComponent(body)}`
}

/** One brief's live data: the views of its refs, a resolver over them, and deciding a card over HTTP. */
const useBriefData = (workspaceId: string, projectId: string, refs: ReadonlyArray<string>) => {
  const queryClient = useQueryClient()
  const q = useQuery(refViewsQuery(workspaceId, projectId, refs))
  const views: Views = useMemo(() => new Map((q.data ?? []).map((v) => [v.ref, v])), [q.data])
  const resolve: RefResolver = useCallback(
    (ref) => {
      const view = views.get(ref)
      return view ? { tone: refTone(ref), title: view.title, view } : null
    },
    [views],
  )
  const decide = useCallback(
    async (card: CardShape, decision: ProposalDecision) => {
      const next = await decideCardHttp(workspaceId, projectId, card, decision)
      void queryClient.invalidateQueries({
        queryKey: ["workspaces", workspaceId, "projects", projectId, "refs"],
      })
      void queryClient.invalidateQueries({ queryKey: homeQuery(workspaceId).queryKey })
      return next
    },
    [queryClient, workspaceId, projectId],
  )
  return { views, resolve, decide, loading: q.isPending && refs.length > 0 }
}

/** The providers chips need on Home: the brief's resolver, deciding over HTTP, the project for a fallback lookup. */
function BriefProviders({
  workspaceId,
  projectId,
  resolve,
  decide,
  children,
}: {
  workspaceId: string
  projectId: string
  resolve: RefResolver
  decide: (card: CardShape, decision: ProposalDecision) => Promise<CardShape | null>
  children: ReactNode
}) {
  const lookup = useMemo(() => ({ workspaceId, projectId }), [workspaceId, projectId])
  return (
    <LinkProjectContext.Provider value={{ workspaceId, projectId }}>
      <RefLookupContext.Provider value={lookup}>
        <RefDecideContext.Provider value={decide}>
          <RefContext.Provider value={resolve}>{children}</RefContext.Provider>
        </RefDecideContext.Provider>
      </RefLookupContext.Provider>
    </LinkProjectContext.Provider>
  )
}

/** A brief's body on Home: its sections live, or (a brief from before 0144) its markdown with live chips. */
export function BriefView({
  body,
  sections,
  workspaceId,
  projectId,
  leadName,
  renderer: Renderer = Markdown,
}: {
  body: string
  sections?: BriefSections | undefined
  workspaceId: string
  projectId: string
  leadName: string
  /** Tests: a static renderer (a static render of the lazy wrapper shows only its fallback). */
  renderer?: ComponentType<MarkdownProps> | undefined
}) {
  const refs = useMemo(() => briefRefs(body, sections), [body, sections])
  const data = useBriefData(workspaceId, projectId, refs)
  return (
    <BriefProviders
      workspaceId={workspaceId}
      projectId={projectId}
      resolve={data.resolve}
      decide={data.decide}
    >
      {sections ? (
        <BriefSectionsView
          sections={sections}
          views={data.views}
          workspaceId={workspaceId}
          projectId={projectId}
          leadName={leadName}
          renderer={Renderer}
        />
      ) : (
        <Renderer text={body} className="md-sm text-ink-2" />
      )}
    </BriefProviders>
  )
}

/** The sections, in order; empty ones are left out. Exported for tests (pass the views directly). */
export function BriefSectionsView({
  sections,
  views,
  workspaceId,
  projectId,
  leadName,
  renderer: Renderer = Markdown,
  decide,
}: {
  sections: BriefSections
  views: Views
  workspaceId: string
  projectId: string
  leadName: string
  renderer?: ComponentType<MarkdownProps> | undefined
  /** Tests: decide without the HTTP route. */
  decide?: ((card: CardShape, decision: ProposalDecision) => Promise<CardShape | null>) | undefined
}) {
  const narrow = useNarrow()
  const asks = sections.asks ?? []
  const waiting = rankWaitingViews(sections.waiting ?? [], views)
  const didntWork = sections.didntWork ?? []
  const happened = sections.happened ?? []
  const next = sections.next ?? []
  const openAsks = asks.filter((r) => views.get(r)?.card?.open).length
  const section = (props: Omit<SectionProps, "workspaceId" | "projectId" | "leadName" | "narrow">) => (
    <BriefSection
      {...props}
      workspaceId={workspaceId}
      projectId={projectId}
      leadName={leadName}
      narrow={narrow}
    />
  )
  return (
    <div data-brief-sections="" className="flex flex-col">
      <p
        data-brief-headline=""
        className="pb-3 font-serif text-[20px] leading-[1.35] text-ink max-sm:text-[18px]"
      >
        <RefText text={sections.headline} />
      </p>
      {asks.length > 0 &&
        section({
          id: "asks",
          title: BRIEF_SECTION_TITLES.asks,
          count:
            openAsks === asks.length
              ? `${openAsks} open`
              : `${openAsks} open · ${asks.length - openAsks} done since`,
          quote: asks.map((r) => views.get(r)?.card?.ask ?? r).join("\n"),
          first: views.get(asks[0] ?? "")?.title ?? "",
          defaultOpen: true,
          children: asks.map((ref) => (
            <AskEntry key={ref} refText={ref} view={views.get(ref)} decide={decide} />
          )),
        })}
      {waiting.top.length + waiting.rest.length > 0 &&
        section({
          id: "waiting",
          title: `${BRIEF_SECTION_TITLES.waiting}, most blocking first`,
          count: `${waiting.top.length + waiting.rest.length}`,
          quote: [...waiting.top, ...waiting.rest]
            .map((r) => `${views.get(r)?.title ?? ""} (${r})`)
            .join("\n"),
          first: waiting.top[0] ? `Top: ${views.get(waiting.top[0])?.title ?? waiting.top[0]}` : "",
          children: (
            <>
              {waiting.top.map((ref) => (
                <WaitingEntry key={ref} refText={ref} view={views.get(ref)} />
              ))}
              {waiting.rest.length > 0 && (
                <Entry mark="wait">
                  <span className="text-ink-2">
                    {waiting.top.length > 0
                      ? `${waiting.rest.length} more, not blocking anything: `
                      : `${waiting.rest.length} not blocking anything yet: `}
                    {waiting.rest.map((r) => (
                      <RefChip key={r} refText={r} />
                    ))}
                  </span>
                </Entry>
              )}
            </>
          ),
        })}
      {didntWork.length > 0 &&
        section({
          id: "didntWork",
          title: BRIEF_SECTION_TITLES.didntWork,
          count: `${didntWork.length}`,
          quote: didntWork.map((f) => f.text).join("\n"),
          first: didntWork[0]?.text ?? "",
          children: didntWork.map((f, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the lead's lines, in order
            <Entry key={i} mark="blocked">
              <span>
                <RefText text={f.text} /> <Chips refs={f.refs} />
              </span>
              {f.needed && (
                <span className="mt-0.5 block text-[13px] text-ink-2">
                  <b className="font-semibold text-ink">Needed:</b> <RefText text={f.needed} />
                </span>
              )}
            </Entry>
          )),
        })}
      {happened.length > 0 &&
        section({
          id: "happened",
          title: BRIEF_SECTION_TITLES.happened,
          count: `${happened.length}`,
          quote: happened.map((l) => l.text).join("\n"),
          first: happened[0]?.text ?? "",
          children: happened.map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the lead's lines, in order
            <Entry key={i} mark={markOfRefs(l.refs, views) ?? "done"}>
              <span>
                <RefText text={l.text} />
              </span>
              {(l.refs?.length ?? 0) > 0 && (
                <details className="mt-1 text-[13px]">
                  <summary className="tap cursor-pointer text-muted hover:text-ink">Evidence</summary>
                  <ul className="mt-1.5 flex list-none flex-col gap-1 rounded-control bg-fill-soft px-2.5 py-2 text-ink-2">
                    {(l.refs ?? []).map((r) => (
                      <li key={r} className="flex flex-wrap items-center gap-1.5">
                        <RefChip refText={r} />
                        <span>{views.get(r)?.title ?? ""}</span>
                        {views.get(r) && <span className="text-muted">· {views.get(r)?.status}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </Entry>
          )),
        })}
      {next.length > 0 &&
        section({
          id: "next",
          title: BRIEF_SECTION_TITLES.next,
          count: "",
          quote: next.map((l) => l.text).join("\n"),
          first: next[0]?.text ?? "",
          children: next.map((l, i) => {
            const waits = (l.refs ?? []).some((r) => views.get(r)?.mark === "needs")
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: the lead's lines, in order
              <Entry key={i} mark={waits ? "needs" : "wait"}>
                <span>
                  <RefText text={l.text} /> <Chips refs={l.refs} />
                  {waits && <span className="text-[12px] text-muted"> · live: waiting on you</span>}
                </span>
              </Entry>
            )
          }),
        })}
      {sections.text?.trim() &&
        section({
          id: "text",
          title: BRIEF_SECTION_TITLES.text,
          count: "",
          quote: sections.text,
          first: sections.text.split("\n")[0] ?? "",
          children: <Renderer text={sections.text} className="md-sm text-ink-2" />,
        })}
    </div>
  )
}

const markOfRefs = (refs: ReadonlyArray<string> | undefined, views: Views): RefView["mark"] | null => {
  const marks = (refs ?? []).map((r) => views.get(r)?.mark)
  if (marks.includes("blocked")) return "blocked"
  if (marks.includes("live")) return "live"
  return null
}

function Chips({ refs }: { refs: ReadonlyArray<string> | undefined }) {
  if (!refs || refs.length === 0) return null
  return (
    <>
      {refs.map((r) => (
        <RefChip key={r} refText={r} />
      ))}
    </>
  )
}

/** One entry: its status mark in the gutter, then the content. */
function Entry({
  mark,
  resolved = false,
  children,
}: {
  mark: RefView["mark"]
  resolved?: boolean
  children: ReactNode
}) {
  return (
    <li
      data-entry-mark={mark}
      className={cn(
        "grid grid-cols-[18px_1fr] gap-x-2 border-t border-rule py-2.5 text-[14.5px] first:border-t-0",
        resolved && "text-muted",
      )}
    >
      <span className="pt-[7px]">
        <MarkGlyph mark={mark} />
      </span>
      <div className="min-w-0">{children}</div>
    </li>
  )
}

/** An ask: the real card in its shape (decided here), or the ref while it loads. */
function AskEntry({
  refText,
  view,
  decide,
}: {
  refText: string
  view: RefView | undefined
  decide?: ((card: CardShape, decision: ProposalDecision) => Promise<CardShape | null>) | undefined
}) {
  const fromContext = useContext(RefDecideContext)
  const card = view?.card
  if (!view || !card)
    return (
      <Entry mark="wait">
        <RefChip refText={refText} />
      </Entry>
    )
  return (
    <Entry mark={card.open ? view.mark : "done"} resolved={!card.open}>
      <div className="flex flex-col gap-1">
        <CardShapeView
          shape={card}
          decide={decide ?? fromContext}
          layout="entry"
          openHref={view.href}
          askExtra={<RefChip refText={refText} />}
        />
      </div>
    </Entry>
  )
}

/** A waiting entry: the thing named, its chip and what it holds up. */
function WaitingEntry({ refText, view }: { refText: string; view: RefView | undefined }) {
  const holds = view?.holds ?? 0
  return (
    <Entry mark={view?.mark ?? "wait"}>
      <span>
        {view?.card ? view.card.ask : (view?.title ?? "")} <RefChip refText={refText} />
        {holds > 0 && (
          <span className="ml-1 inline-flex items-center rounded-pill bg-risk-soft px-1.5 text-[12px] text-risk-text">
            blocks {holds}
          </span>
        )}
      </span>
    </Entry>
  )
}

interface SectionProps {
  id: string
  title: string
  count: string
  /** What the reply quotes. */
  quote: string
  /** The one line a folded section shows (phone). */
  first: string
  defaultOpen?: boolean
  workspaceId: string
  projectId: string
  leadName: string
  narrow: boolean
  children: ReactNode
}

/** A section: its heading and count, its entries, and a reply line to the lead's chat. Folded on a phone, but asks. */
function BriefSection({
  id,
  title,
  count,
  quote,
  first,
  defaultOpen = false,
  workspaceId,
  projectId,
  leadName,
  narrow,
  children,
}: SectionProps) {
  const head = (
    <span className="flex items-baseline justify-between gap-2">
      <span className="kicker text-ink">{title}</span>
      {count && <span className="text-[12px] text-muted">{count}</span>}
    </span>
  )
  const body = (
    <>
      <ul className="m-0 list-none p-0">{children}</ul>
      <ReplyLine
        workspaceId={workspaceId}
        projectId={projectId}
        leadName={leadName}
        title={title.split(" · ")[0] ?? title}
        quote={quote}
      />
    </>
  )
  return (
    <section data-brief-section={id} aria-label={title} className="border-t border-rule py-3.5">
      {narrow && !defaultOpen ? (
        <details>
          <summary className="tap cursor-pointer list-none">
            {head}
            <span className="mt-0.5 block truncate text-[13.5px] text-ink-2">{first}</span>
          </summary>
          <div className="mt-2">{body}</div>
        </details>
      ) : (
        <>
          <div className="mb-1.5">{head}</div>
          {body}
        </>
      )}
    </section>
  )
}

/** "Reply to Lea about your asks…": opens her chat with the section quoted (the composer, unsent). */
function ReplyLine({
  workspaceId,
  projectId,
  leadName,
  title,
  quote,
}: {
  workspaceId: string
  projectId: string
  leadName: string
  title: string
  quote: string
}) {
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined
  const [text, setText] = useState("")
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const href = replyHref(workspaceId, projectId, `${title}:\n${quote}`, text.trim())
    if (router) router.history.push(href)
    else window.location.assign(href)
  }
  return (
    <form
      onSubmit={submit}
      className="mt-2 flex items-center gap-2 rounded-control border border-dashed border-line-control py-1 pr-1 pl-2.5 text-[13px] text-muted focus-within:border-solid focus-within:border-ink"
    >
      <Glyph name="reply" size={13} />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label={`Reply to ${leadName} about ${title.toLowerCase()}`}
        placeholder={`Reply to ${leadName} about ${title.toLowerCase()}…`}
        className="min-w-0 flex-1 bg-transparent py-1 text-ink placeholder:text-placeholder max-sm:text-[16px]"
      />
      {text.trim() && (
        <button
          type="submit"
          className="tap rounded-control px-2 py-1 text-[12.5px] font-semibold text-ink hover:bg-fill"
        >
          Reply in chat
        </button>
      )}
    </form>
  )
}
